import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { z } from "zod";
import { ArrowLeft, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { useAllTenants } from "@/hooks/use-tenants";
import { CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { tenantLabel, tenantOptionValue } from "@/lib/cells/cells";
import { useImportJob } from "@/hooks/use-itsm-import";
import { ImportJobForm } from "@/components/itsm-import/import-job-form";

const searchSchema = z.object({
  tenantId: z.string().optional(),
  cloneFrom: z.string().optional(),
});

export const Route = createFileRoute("/itsm-import_/new")({
  component: NewImportPage,
  validateSearch: zodValidator(searchSchema),
});

function NewImportPage() {
  const { tenantId: searchTenantId, cloneFrom } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false);

  const selectedTenantId = searchTenantId ?? "";

  const { data: tenants, isLoading: tenantsLoading, failures: tenantFailures } = useAllTenants();
  const { data: sourceJob } = useImportJob(selectedTenantId, cloneFrom ?? null);

  const sortedTenants = useMemo(() => {
    if (!tenants) return [];
    return [...tenants].sort((a, b) => a.company.localeCompare(b.company));
  }, [tenants]);

  const selectedTenant = tenants?.find((t) => t.id === selectedTenantId);

  return (
    <div className="container mx-auto max-w-3xl space-y-6 p-6">
      {/* Back link */}
      <Link
        to="/itsm-import"
        className="-ml-2 inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Jobs
      </Link>

      <div>
        <h1 className="text-2xl font-bold">New Import</h1>
        <p className="text-muted-foreground">
          Configure and start a new ticket import
        </p>
      </div>

      {/* Tenant Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Select Tenant</CardTitle>
          <CardDescription>
            Choose the tenant to import tickets for
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Popover open={tenantPopoverOpen} onOpenChange={setTenantPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={tenantPopoverOpen}
                className="w-full justify-between"
              >
                <span className="truncate">
                  {selectedTenant ? tenantLabel(selectedTenant) : "Select a tenant..."}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
              <Command>
                <CellTenantsUnavailable failures={tenantFailures} />
                <CommandInput placeholder="Search tenants..." />
                <CommandList>
                  <CommandEmpty>
                    {tenantsLoading ? "Loading..." : "No tenant found."}
                  </CommandEmpty>
                  <CommandGroup>
                    {sortedTenants.map((tenant) => (
                      <CommandItem
                        key={tenant.id}
                        value={tenantOptionValue(tenant)}
                        onSelect={() => {
                          void navigate({ search: { tenantId: tenant.id } });
                          setTenantPopoverOpen(false);
                        }}
                        className={cn(selectedTenantId === tenant.id && "bg-accent")}
                      >
                        {tenantLabel(tenant)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </CardContent>
      </Card>

      {/* Import Form */}
      {selectedTenantId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Import Configuration</CardTitle>
            <CardDescription>
              Configure the import for {selectedTenant && tenantLabel(selectedTenant)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ImportJobForm
              tenantId={selectedTenantId}
              onSuccess={(jobId) => { void navigate({ to: "/itsm-import/$jobId", params: { jobId }, search: { tenantId: selectedTenantId } }); }}
              initialValues={sourceJob ? {
                source: sourceJob.source,
                provider: sourceJob.provider ?? undefined,
                credential_id: sourceJob.credential_id ?? undefined,
                source_project: sourceJob.source_project ?? undefined,
                record_types: sourceJob.record_types ?? undefined,
                custom_field_mappings: sourceJob.custom_field_mappings ?? null,
                sync_linked_tickets: sourceJob.sync_linked_tickets ?? null,
                target_desk_id: sourceJob.target_desk_id,
                target_subdesk_id: sourceJob.target_subdesk_id ?? undefined,
                since_date: sourceJob.since_date?.split("T")[0],
                until_date: sourceJob.until_date?.split("T")[0],
                include_closed: true,
                dry_run: sourceJob.dry_run,
                skip_dedup: sourceJob.skip_dedup,
                rate_limit: sourceJob.rate_limit?.toString(),
                max_tickets: sourceJob.max_tickets?.toString(),
                export_csv: sourceJob.export_csv ?? false,
              } : undefined}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
